import React, { useContext, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react';
import {
  Bars3Icon,
  HomeIcon,
  FlagIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ClockIcon,
  PhotoIcon,
  BellIcon,
  TagIcon,
  TicketIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  KeyIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { firebaseAuth } from '../../../providers/AuthProvider';
import { CurrentEventDataContext } from '../CurrentEventDataContext';
import { DeployTarget, STAFF_ROLE } from '../../../Constants';
import ThemeToggle from '../../../theme/ThemeToggle';
import { isAssignedToEvent } from '../../../utils/eventAssignment';

const AdminShell = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, handleSignout } = useContext(firebaseAuth);
  const { currentEventData } = useContext(CurrentEventDataContext);
  const location = useLocation();
  const navigate = useNavigate();

  const eventId = currentEventData?.id;
  const eventName = Number(user?.role) === STAFF_ROLE ? '' : currentEventData?.sysname || currentEventData?.name;

  const navigation = useMemo(() => {
    const role = user?.role;
    if (Number(role) === STAFF_ROLE) return {
      primary: [
        { name: 'Assigned events', to: '/manage', icon: HomeIcon, end: true },
        { name: 'Registrations', to: /^\/manage\/registrations\/[\w-]+$/.test(location.pathname) ? location.pathname : '/manage/registrations', icon: ClipboardDocumentListIcon },
        { name: 'Check-In', to: '/checkin', icon: TicketIcon },
      ],
      admin: [],
    };
    const isAdmin = role === 1;
    const isManagerOrBetter = role <= 2 || user?.crmManager === true;
    const assignedCurrent = isAssignedToEvent(currentEventData, user?.email);
    const canEditCurrent = isAdmin || assignedCurrent;
    const isStaff = role !== 5;

    const items = [
      { name: 'Dashboard', to: '/manage', icon: HomeIcon, show: isStaff, end: true },
      { name: 'Event', to: eventId ? `/manage/event/${eventId}` : '/manage', icon: FlagIcon, show: isManagerOrBetter && !!eventId },
      { name: 'Tickets', to: eventId ? `/manage/tickets/${eventId}` : '/manage', icon: TicketIcon, show: isManagerOrBetter && canEditCurrent && !!eventId },
      { name: 'Registrations', to: eventId ? `/manage/registrations/${eventId}` : '/manage/registrations', icon: ClipboardDocumentListIcon, show: isStaff },
      { name: 'Event Recaps', to: '/manage/event-recaps', icon: DocumentTextIcon, show: true },
      { name: 'Sessions', to: eventId ? `/manage/sessions/${eventId}` : '/manage/sessions', icon: ClockIcon, show: isManagerOrBetter && canEditCurrent && !!currentEventData?.allowCapacity && currentEventData?.capacityType === 'session' },
      { name: 'Gallery', to: eventId ? `/manage/gallery/${eventId}` : '/manage/gallery', icon: PhotoIcon, show: isManagerOrBetter && canEditCurrent && !!currentEventData?.allowGallery },
    ];

    const adminItems = [
      { name: 'Customer Care', to: '/manage/customer-care', icon: BellIcon, show: isAdmin || user?.crmManager === true },
      { name: 'Campaign Codes', to: '/manage/campaign-codes', icon: TagIcon, show: isAdmin || user?.crmManager === true },
      { name: 'Lead Activity', to: '/manage/lead-activity-report', icon: ChartBarIcon, show: true },
      { name: 'Settings', to: '/manage/settings', icon: Cog6ToothIcon, show: isAdmin },
      { name: 'Dealers', to: '/manage/dealers', icon: KeyIcon, show: isAdmin },
      { name: 'Users', to: '/manage/users', icon: UsersIcon, show: isAdmin },
    ];

    return {
      primary: items.filter((i) => i.show),
      admin: adminItems.filter((i) => i.show),
    };
  }, [user, eventId, currentEventData, location.pathname]);

  const isCurrent = (to, end) => {
    if (end) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const NavItem = ({ item, onClick }) => (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onClick}
        className={({ isActive }) =>
          clsx(
            'group flex min-h-11 items-center justify-start gap-x-3 rounded-md px-3 py-2 text-left text-sm font-semibold',
            (isActive || isCurrent(item.to, item.end))
              ? 'bg-white/10 text-zinc-50'
              : 'text-zinc-400 hover:bg-white/5 hover:text-white',
          )
        }
      >
        <item.icon
          aria-hidden="true"
          className="size-5 shrink-0 text-zinc-400 group-hover:text-white"
        />
        <span className="truncate">{item.name}</span>
      </NavLink>
    </li>
  );

  const SidebarBody = ({ onItemClick }) => (
    <div className="manage-sidebar flex grow flex-col gap-y-5 overflow-y-auto bg-zinc-950 px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[env(safe-area-inset-top)] ring-1 ring-white/10">
      <div className="flex h-16 shrink-0 items-center gap-3 px-3">
        <Link to="/manage" className="flex items-baseline gap-2" onClick={onItemClick}>
          <span className="text-base font-semibold tracking-tight text-zinc-50">Porsche</span>
          <span className="text-base text-zinc-400">eZentrum</span>
        </Link>
        {DeployTarget === 'staging' && (
          <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400 ring-1 ring-amber-500/30">
            staging
          </span>
        )}
      </div>
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-6">
          <li>
            <ul className="space-y-1">
              {navigation.primary.map((item) => (
                <NavItem key={item.name} item={item} onClick={onItemClick} />
              ))}
            </ul>
          </li>
          {navigation.admin.length > 0 && (
            <li>
              <div className="px-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Administration
              </div>
              <ul className="space-y-1 pt-2">
                {navigation.admin.map((item) => (
                  <NavItem key={item.name} item={item} onClick={onItemClick} />
                ))}
              </ul>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );

  return (
    <div
      id="dashboard-layout"
      className="min-h-screen min-w-0 overflow-x-clip bg-zinc-50 font-sans text-zinc-900 antialiased dark:bg-zinc-950 dark:text-zinc-100"
    >
      {/* Mobile sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-zinc-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
        />
        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition-transform duration-200 ease-out data-closed:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="size-6 text-zinc-50" />
                </button>
              </div>
            </TransitionChild>
            <SidebarBody onItemClick={() => setSidebarOpen(false)} />
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar */}
      <div className="max-lg:hidden fixed inset-y-0 left-0 z-50 flex w-64 flex-col">
        <SidebarBody />
      </div>

      <div className="min-w-0 lg:pl-64">
        {/* Topbar */}
        <div className="sticky top-0 z-40 flex min-h-[calc(4rem+env(safe-area-inset-top))] shrink-0 items-center gap-x-3 border-b border-zinc-200 bg-zinc-50 px-4 pt-[env(safe-area-inset-top)] sm:gap-x-6 sm:px-6 lg:px-8 dark:border-zinc-800 dark:bg-zinc-900">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-zinc-700 lg:hidden dark:text-zinc-300"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div aria-hidden="true" className="h-6 w-px bg-zinc-200 lg:hidden dark:bg-zinc-700" />
          <div className="flex min-w-0 flex-1 items-center gap-x-2 self-stretch sm:gap-x-4">
            <div
              className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100"
              title={eventName || ''}
            >
              {eventName ? eventName : ''}
            </div>
            <div className="shrink-0">
              <ThemeToggle />
            </div>
            <Menu as="div" className="relative shrink-0">
              <MenuButton className="flex items-center gap-x-2 rounded-md p-1.5 text-sm font-semibold text-zinc-900 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-800">
                <span className="sr-only">Open user menu</span>
                <span className="inline-flex size-8 items-center justify-center rounded-full bg-zinc-900 text-xs font-semibold text-zinc-50">
                  {(user?.email || '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden lg:inline-flex lg:items-center lg:gap-x-1">
                  <span aria-hidden="true" className="text-sm/6 font-semibold text-zinc-900 dark:text-zinc-100">
                    {user?.email}
                  </span>
                  <ChevronDownIcon aria-hidden="true" className="size-5 text-zinc-500 dark:text-zinc-400" />
                </span>
              </MenuButton>
              <MenuItems
                transition
                anchor="bottom end"
                className="z-50 mt-2 w-56 origin-top-right rounded-md bg-zinc-50 py-1 shadow-lg ring-1 ring-black/5 transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in dark:bg-zinc-900 dark:ring-white/10"
              >
                <MenuItem>
                  {({ focus }) => (
                    <Link
                      to="/manage/profile"
                      className={clsx(
                        'block px-3 py-2 text-sm',
                        focus
                          ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100'
                          : 'text-zinc-700 dark:text-zinc-300',
                      )}
                    >
                      Your profile
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={async () => {
                        await handleSignout();
                        navigate('/manage/sign-in');
                      }}
                      className={clsx(
                        'block w-full px-3 py-2 text-left text-sm',
                        focus
                          ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100'
                          : 'text-zinc-700 dark:text-zinc-300',
                      )}
                    >
                      Sign out
                    </button>
                  )}
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>

        <main className="min-w-0 py-4 sm:py-8">
          <div className="min-w-0 px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
